"use client";

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ExternalLinkIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { imagineStore } from "@/stores/imagine.store";
import { imagineConfigStore } from "@/stores/imagine-config.store";
import { cn } from "@/lib/utils";
import { useSiteInfo } from "@/hooks/use-site-info";
import { aiServiceFetch } from "@/lib/ai-service-client";
import { HttpError } from "@/lib/http-error";
import { SpinnerIcon } from "@/components/icons/spinner";
import { DeploymentLogs } from "./deployment-logs";
import { DeploymentStatus, type Models } from "@appwrite.io/console";
import type { GetSiteInfoResponseBody } from "@imagine/ai-service/shared-types/route-types";
import { toast } from "sonner";

function getAppwriteConsoleUrl({
  projectId,
  region,
}: {
  projectId: string;
  region: string;
}) {
  return `/console/project-${region}-${projectId}/sites/site-project-${projectId}`;
}

function getRelativeTimeString(dateString: string): string {
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((Date.now() - date.getTime()) / 1000);

  const units = [
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 },
  ];

  for (const { label, seconds } of units) {
    const value = Math.floor(diffInSeconds / seconds);
    if (value >= 1) {
      return `${value} ${label}${value > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
}

type ReleaseSuccessResponse = {
  success: true;
  data?: {
    deployment?: Models.Deployment;
  };
};

type ReleaseErrorResponse = {
  success?: false;
  error?: string;
};

type ReleaseResponse = ReleaseSuccessResponse | ReleaseErrorResponse;

function isReleaseSuccessResponse(
  response: ReleaseResponse,
): response is ReleaseSuccessResponse {
  return (
    typeof response === "object" && !!response && response.success === true
  );
}

export function ReleaseButton({ disabled }: { disabled?: boolean }) {
  const config = imagineConfigStore.use.config();
  const [isReleasePopoverOpen, setIsReleasePopoverOpen] = useState(false);
  const [isDomainActionLoading, setIsDomainActionLoading] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [activeDeployment, setActiveDeployment] =
    useState<Models.Deployment | null>(null);

  const queryClient = useQueryClient();
  const { data: siteInfo, refetch: refetchSiteInfo } = useSiteInfo();
  const projectId = imagineStore.use.projectId();

  const appwriteSitesBaseUrl = new URL(config!.APPWRITE_SITES_BASE_URL);
  const stageDomain = `imagine-${projectId}.${appwriteSitesBaseUrl.hostname}`;
  const displayDomain = siteInfo?.primaryDomain || stageDomain;
  const displayDomainUrl = displayDomain ? `https://${displayDomain}` : null;
  const hasCustomDomains = (siteInfo?.customDomainsCount ?? 0) > 0;

  const deployment = siteInfo?.latestDeployment;
  const deploymentStatus = deployment?.status;

  const isDeploying =
    deploymentStatus === DeploymentStatus.Building ||
    deploymentStatus === DeploymentStatus.Processing ||
    deploymentStatus === DeploymentStatus.Waiting;

  const isDeploymentReady = deploymentStatus === DeploymentStatus.Ready;
  const isDeploymentFailed = deploymentStatus === DeploymentStatus.Failed;

  const isDomainActionEnabled =
    siteInfo?.siteExists && siteInfo?.hasDeployment && !isDeploying;

  const updateSiteInfoCache = (newDeployment: Models.Deployment) => {
    queryClient.setQueryData<GetSiteInfoResponseBody>(
      ["site-info", projectId],
      (oldDeployment) => {
        if (!oldDeployment) return oldDeployment;
        return {
          ...oldDeployment,
          latestDeployment: newDeployment,
          hasDeployment: true,
        };
      },
    );
  };

  const handleReleaseSuccess = (deployment: Models.Deployment) => {
    setActiveDeployment(deployment);
    updateSiteInfoCache(deployment);
  };

  const handleLogsUpdate = (updatedLogs: string) => {
    if (activeDeployment) {
      const updatedDeployment = {
        ...activeDeployment,
        buildLogs: updatedLogs,
      };
      setActiveDeployment(updatedDeployment);
      updateSiteInfoCache(updatedDeployment);
    }
  };

  const { mutate: releaseArtifact, isPending: isReleasing } = useMutation<
    ReleaseSuccessResponse,
    Error,
    void
  >({
    mutationFn: async () => {
      if (!projectId) {
        throw new Error("Missing project or artifact context");
      }

      const response = await aiServiceFetch(
        `/api/projects/${projectId}/deployments`,
        {
          method: "POST",
          body: JSON.stringify({ commitSha: "main" }),
        },
      );

      if (!response.ok) {
        throw await HttpError.fromResponse(response);
      }

      const parsed = (await response.json()) as ReleaseResponse;

      if (!isReleaseSuccessResponse(parsed)) {
        throw new Error("Failed to release artifact");
      }

      return parsed;
    },
    onSuccess: (data) => {
      if (data.data?.deployment) {
        handleReleaseSuccess(data.data.deployment);
      }
    },
  });

  const showReleasingState = isReleasing || isDeploying || showLogs;

  const currentDeployment = useMemo(
    () => activeDeployment || (isDeploying ? deployment : null),
    [activeDeployment, isDeploying, deployment],
  );

  useEffect(() => {
    if (showReleasingState) {
      setShowLogs(true);
    }
  }, [showReleasingState]);

  return (
    <Popover
      open={isReleasePopoverOpen}
      onOpenChange={async (open) => {
        setIsReleasePopoverOpen(open);
        if (open) {
          const result = await refetchSiteInfo();
          const latestDeployment = result.data?.latestDeployment as
            | Models.Deployment
            | undefined;
          const latestStatus = latestDeployment?.status;

          const isActiveDeployment =
            latestStatus === DeploymentStatus.Building ||
            latestStatus === DeploymentStatus.Processing ||
            latestStatus === DeploymentStatus.Waiting;

          if ((isActiveDeployment && latestDeployment) || activeDeployment) {
            if (latestDeployment && isActiveDeployment) {
              setActiveDeployment(latestDeployment);
            }
            setShowLogs(true);
          }
        } else {
          if (!isDeploying && !activeDeployment) {
            setShowLogs(false);
            setActiveDeployment(null);
          }
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          size="sm"
          className={cn(
            "px-2.5 py-1.5 rounded-lg border border-foreground bg-foreground text-background leading-[140%] tracking-[-0.063px] shadow-none before:hidden w-auto",
          )}
          disabled={disabled}
        >
          {showReleasingState ? "Releasing..." : "Release"}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn(
          "p-0 transition-all duration-300",
          showLogs ? "w-[482px]" : "w-72",
        )}
      >
        <div className="flex flex-col">
          <div
            className={cn(
              "px-4 pt-4 pb-3 transition-all duration-700 ease-out overflow-hidden",
              showLogs ? "opacity-0 max-h-0 py-0" : "opacity-100 max-h-[200px]",
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 space-y-1.5">
                {displayDomainUrl ? (
                  <a
                    href={displayDomainUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
                    title={displayDomain ?? undefined}
                  >
                    <span className="flex-1 truncate">{displayDomain}</span>
                    <ExternalLinkIcon className="size-4 shrink-0 text-primary" />
                  </a>
                ) : (
                  <p
                    className="block truncate text-sm font-medium text-foreground"
                    title="Release domain unavailable"
                  >
                    Release domain unavailable
                  </p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span
                    className={cn(
                      "size-2 rounded-full border border-current",
                      isDeploymentReady &&
                        !showReleasingState &&
                        "bg-green-500 border-green-500",
                      showReleasingState && "bg-yellow-500 border-yellow-500",
                      isDeploymentFailed &&
                        !showReleasingState &&
                        "bg-red-500 border-red-500",
                    )}
                  />
                  <span>
                    {showReleasingState
                      ? "Release in progress..."
                      : isDeploymentFailed
                        ? "Release failed"
                        : deployment?.$updatedAt
                          ? `Released ${getRelativeTimeString(deployment.$updatedAt)}`
                          : "Not published"}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            className={cn(
              "px-4 py-3 overflow-hidden transition-all duration-700 ease-out",
              !showLogs && "border-t border-border",
            )}
          >
            <div className="relative h-[34px] flex items-center">
              <div
                className={cn(
                  "transition-all duration-700 ease-out",
                  showReleasingState ? "flex-0" : "flex-1",
                )}
              />

              <div
                className={cn(
                  "transition-all duration-700 ease-out overflow-hidden",
                  showReleasingState || showLogs
                    ? "w-0 opacity-0 -translate-x-8 mr-0"
                    : "opacity-100 translate-x-0 mr-2",
                )}
              >
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="px-2.5 py-1.5 rounded-lg !border-border bg-background text-muted-foreground leading-[140%] tracking-[-0.063px] hover:bg-accent hover:text-accent-foreground shadow-none whitespace-nowrap"
                  loading={isDomainActionLoading}
                  disabled={!isDomainActionEnabled}
                  onClick={async () => {
                    setIsDomainActionLoading(true);
                    try {
                      const callbacks = imagineConfigStore.getState().callbacks;
                      if (hasCustomDomains) {
                        await callbacks?.onManageDomains?.(
                          siteInfo?.primaryDomain,
                        );
                      } else {
                        await callbacks?.onAddDomain?.();
                      }
                    } finally {
                      setIsDomainActionLoading(false);
                      setIsReleasePopoverOpen(false);
                    }
                  }}
                >
                  {hasCustomDomains ? "Manage Domains" : "Add Domain"}
                </Button>
              </div>

              <div
                className={cn(
                  "transition-all duration-700 ease-out overflow-hidden",
                  showReleasingState || showLogs
                    ? "w-0 opacity-0 mr-0"
                    : "opacity-100 mr-0",
                )}
              >
                <button
                  type="button"
                  disabled={!displayDomainUrl}
                  onClick={() => releaseArtifact()}
                  className="whitespace-nowrap text-sm leading-[140%] tracking-[-0.063px] py-1.5 px-2.5 rounded-lg border border-foreground bg-foreground text-background"
                >
                  Release
                </button>
              </div>

              <div
                className={cn(
                  "transition-all duration-700 ease-out whitespace-nowrap text-sm leading-[140%] tracking-[-0.063px] text-foreground",
                  showReleasingState
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 -translate-x-4 w-0",
                )}
              >
                Releasing...
              </div>

              <div
                className={cn(
                  "transition-all duration-700 ease-out",
                  showReleasingState ? "flex-1" : "flex-0 w-0",
                )}
              />

              <div
                className={cn(
                  "transition-all duration-500 delay-200",
                  showReleasingState
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-4 w-0",
                )}
              >
                <SpinnerIcon className="size-[20px] animate-spin" />
              </div>
            </div>
          </div>
          {showLogs && (
            <DeploymentLogs
              deploymentId={currentDeployment?.$id}
              initialLogs={currentDeployment?.buildLogs}
              onLogsUpdate={handleLogsUpdate}
              onComplete={(deployment) => {
                setShowLogs(false);
                setActiveDeployment(null);
                updateSiteInfoCache(deployment);
                setIsReleasePopoverOpen(false);
              }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
